import { Building2, MapPin, Plus, TriangleAlert } from 'lucide-react';
import ReactSelect from 'react-select';
import LocationMiniMap, { type MapPoint } from '../components/LocationMiniMap';
import { selectSovereignStyles } from '../selectStyles';
import type { LocationOption } from '../types';
import { HelperText } from '../ui';

export default function LocationStep(props: {
  locationOptions: LocationOption[];
  locationSelection: LocationOption[];
  setLocationSelection: (v: LocationOption[]) => void;
  loadingMasters: boolean;
  onAddLocation: () => void;
}) {
  const { locationOptions, locationSelection, setLocationSelection, loadingMasters, onAddLocation } = props;

  const points: MapPoint[] = locationSelection
    .filter((l): l is LocationOption & { lat: number; lng: number } => l.lat != null && l.lng != null)
    .map((l) => ({
      id: l.value,
      name: l.name,
      lat: l.lat,
      lng: l.lng,
      primary: locationSelection[0]?.value === l.value,
    }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
          Site locations
          {locationSelection.length > 0 && (
            <span className="ml-2 font-normal normal-case tracking-normal text-[#7C8A82]">{locationSelection.length} selected</span>
          )}
        </label>
        <button
          type="button"
          onClick={onAddLocation}
          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold transition"
          style={{ background: 'rgba(51,146,116,0.12)', color: '#1F5A47' }}
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          Add new site
        </button>
      </div>

      <ReactSelect<LocationOption, true>
        isMulti
        options={locationOptions}
        value={locationSelection}
        onChange={(opts) => setLocationSelection(opts ? [...opts] : [])}
        placeholder={loadingMasters ? 'Loading…' : 'Search and select one or more sites…'}
        styles={selectSovereignStyles<LocationOption, true>()}
        isDisabled={loadingMasters}
        isClearable
        isSearchable
        className="text-sm"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        noOptionsMessage={() => 'No locations found'}
      />
      <HelperText>The first selected site is stored as the primary location.</HelperText>

      {locationSelection.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="relative rounded-2xl p-3" style={{ border: '1px solid rgba(11,59,46,0.35)', background: 'rgba(250,247,238,0.96)' }}>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#0B3B2E]">Selected sites</p>
            <ol className="space-y-1.5">
              {locationSelection.map((loc, i) => (
                <li key={loc.value} className="flex items-center gap-2 text-sm text-[#2A1F17]">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold" style={{ background: 'rgba(51,146,116,0.14)', color: '#1F5A47' }}>
                    {i === 0 ? '★' : i + 1}
                  </span>
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-[#7C8A82]" />
                  <span className="min-w-0 truncate font-medium">
                    {loc.name}
                    {loc.state ? <span className="text-[#7C8A82]"> · {loc.state}</span> : null}
                  </span>
                  {loc.lat != null && loc.lng != null ? (
                    <span className="ml-auto shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[9px] font-bold tabular-nums" style={{ background: 'rgba(51,146,116,0.12)', color: '#1F5A47' }}>
                      {loc.lat.toFixed(3)}, {loc.lng.toFixed(3)}
                    </span>
                  ) : (
                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ background: 'rgba(201,169,97,0.16)', color: '#8A5A08' }}>
                      <TriangleAlert className="h-2.5 w-2.5" />
                      No coordinates
                    </span>
                  )}
                </li>
              ))}
            </ol>
            {points.length < locationSelection.length ? (
              <HelperText tone="warn">Sites without coordinates can't be shown on the map — edit them in Location master to add lat/long.</HelperText>
            ) : null}
          </div>
          <div>
            <LocationMiniMap points={points} height={300} />
            <p className="mt-1.5 text-[10px]" style={{ color: '#7C8A82' }}>
              Region view — ★ primary site shown with the larger pin.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl px-4 py-10 text-center" style={{ border: '1px dashed rgba(11,59,46,0.25)', background: 'rgba(250,247,238,0.6)' }}>
          <Building2 className="mx-auto mb-2 h-6 w-6 text-[#7C8A82]" />
          <p className="text-sm font-medium text-[#7C8A82]">No sites selected yet</p>
          <p className="mt-1 text-xs text-[#7C8A82]">Pick from the list above or add a new site with its map pin.</p>
        </div>
      )}
    </div>
  );
}
